import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAllSeries } from "../utils/seriesUtils";
import { getAllMovies } from "../utils/movieUtils";
import { getAllBooks } from "../utils/bookUtils";
import { getAllComics } from "../utils/comicUtils";
import { useLang } from "../i18n/LanguageContext";
import { handleImgError } from "../utils/thumbnailUtils";
import { PageSEO } from "../hooks/useSEO";
import "../styles/styles.css";

const Search = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang } = useLang();
  const isEN = lang === "en";

  const q = searchParams.get("q") || "";
  const [query, setQuery] = useState(q);
  const [results, setResults] = useState([]);

  useEffect(() => {
    document.title = q ? `"${q}" — AIVEO` : "Search — AIVEO";
    if (!q.trim()) { setResults([]); return; }
    const lower = q.toLowerCase();
    const match = (item) =>
      item.title.toLowerCase().includes(lower) ||
      item.synopsis?.toLowerCase().includes(lower) ||
      item.genre?.some((g) => g.toLowerCase().includes(lower)) ||
      item.author?.toLowerCase().includes(lower);

    const series  = getAllSeries().filter(match).map((i) => ({ ...i, _type: "series" }));
    const movies  = getAllMovies().filter(match).map((i) => ({ ...i, _type: "movies" }));
    const books   = getAllBooks().filter(match).map((i) => ({ ...i, _type: "books" }));
    const comics  = getAllComics().filter(match).map((i) => ({ ...i, _type: "comics" }));
    setResults([...series, ...movies, ...books, ...comics]);
  }, [q]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) setSearchParams({ q: query.trim() });
  };

  const getPath = (item) => `/${item._type}/${item.id}`;
  const typeLabel = (t) => ({ series: "Serie", movies: "Película", books: "Libro", comics: "Cómic" }[t] || t);

  return (
    <div className="search-page">
      <PageSEO
        title={q ? `"${q}" — Búsqueda` : "Buscar contenido IA"}
        description={q
          ? `Resultados para "${q}" en AIVEO — series, películas, libros y cómics creados con inteligencia artificial.`
          : "Busca series, películas, libros y cómics originales creados con IA en AIVEO."}
        url={`/search${q ? `?q=${encodeURIComponent(q)}` : ""}`}
      />
      <div className="search-header">
        <form className="search-form" onSubmit={handleSearch}>
          <input
            className="search-input"
            type="search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={isEN ? "Search series, movies, books, comics…" : "Buscar series, películas, libros, cómics…"}
          />
          <button type="submit" className="search-btn">
            {isEN ? "Search" : "Buscar"}
          </button>
        </form>
      </div>

      <div className="search-results">
        {q && results.length === 0 && (
          <p className="search-empty">
            {isEN
              ? `No results for "${q}". Try a different keyword.`
              : `Sin resultados para "${q}". Prueba con otra palabra clave.`}
          </p>
        )}

        {results.length > 0 && (
          <>
            <p className="search-count">
              {results.length} {isEN ? "result" : "resultado"}{results.length !== 1 ? "s" : ""} {isEN ? "for" : "para"} <strong>"{q}"</strong>
            </p>
            <div className="search-grid">
              {results.map((item) => (
                <div key={`${item._type}-${item.id}`} className="search-card" onClick={() => navigate(getPath(item))}>
                  <div className="search-card-img">
                    <img src={item.thumbnail} alt={item.title} loading="lazy" onError={(e) => handleImgError(e, item.title, 800, 450)} />
                    <span className="search-card-type">{typeLabel(item._type)}</span>
                  </div>
                  <div className="search-card-info">
                    <h3>{item.title}</h3>
                    <p className="search-card-genre">{item.genre?.join(", ") || ""}</p>
                    <p className="search-card-synopsis">{item.synopsis?.slice(0, 90)}…</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!q && (
          <div className="search-placeholder">
            <span className="search-placeholder-icon">🔍</span>
            <p>{isEN ? "Start typing to search all AIVEO content." : "Empieza a escribir para buscar en todo el contenido de AIVEO."}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
